import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DataTable,
  Field,
  HStack,
  Primitive,
  TextField,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import { faSearch } from '@rtkwlf/pro-solid-svg-icons/faSearch';
import type { SecurityFocusWithStateChangeTime } from '@rtkwlf/track-public';
import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import type { TFunction } from 'i18next';

import { EmptyResults } from '@packages/components/empty-results/empty-results';

import { FormattedDate } from '../../../components/formatted-date';
import { LinkButton } from '../../../components/link-button';
import { routes } from '../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

import {
  FocusStatusBadge,
  type FocusStatusBadgeProps,
} from './focus-status-badge';

/* -------------------------------------------------------------------------------------------------
 * Context
 * -----------------------------------------------------------------------------------------------*/
const getColumns = (
  t: TFunction<'translation', undefined>,
): ColumnDef<SecurityFocusWithStateChangeTime>[] => [
  {
    accessorKey: 'name',
    header: t('securityFocuses.focusesTable.focusName'),
  },
  {
    accessorKey: 'detour',
    header: t('securityFocuses.focusesTable.type'),
    cell: ({ row }) => (
      <div>
        {row.original.detour
          ? t('securityFocuses.focusesTable.detour')
          : t('securityFocuses.focusesTable.securityFocus')}
      </div>
    ),
  },
  {
    accessorKey: 'status',
    header: t('securityFocuses.focusesTable.status'),
    cell: ({ row }) => (
      <FocusStatusBadge
        state={row.original.state as FocusStatusBadgeProps['state']}
      />
    ),
  },
  {
    accessorKey: 'dateAdded',
    header: t('securityFocuses.focusesTable.dateAdded'),
    cell: ({ row }) => <FormattedDate timestamp={row.original.dateAdded} />,
  },
  {
    accessorKey: 'completedAt',
    header: t('securityFocuses.focusesTable.dateCompleted'),
    cell: ({ row }) => <FormattedDate timestamp={row.original.completedAt} />,
  },
  {
    accessorKey: 'spidrCount',
    header: t('securityFocuses.focusesTable.totalSpidrs'),
  },
  {
    accessorKey: '_actions',
    header: '',
    cell: ({ row }) => (
      <HStack xAlign="right">
        <LinkButton
          variant="tertiary"
          href={routes.securityFocuses.focusID.index({
            focusID: row.original.id,
          })}
        >
          {row.original.detour
            ? t('securityFocuses.focusesTable.viewDetour')
            : t('securityFocuses.focusesTable.viewFocus')}
        </LinkButton>
      </HStack>
    ),
    enableHiding: false,
    meta: {
      align: 'right',
    },
  },
];

/* -------------------------------------------------------------------------------------------------
 * All Security Focuses Table
 * -----------------------------------------------------------------------------------------------*/

export type AllSecurityFocusesTableProps = {
  focuses: SecurityFocusWithStateChangeTime[];
};

export const AllSecurityFocusesTable: React.FC<
  AllSecurityFocusesTableProps
> = ({ focuses }) => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Init state to track the search
   */
  const [search, setSearch] = React.useState('');

  const rows = React.useMemo((): SecurityFocusWithStateChangeTime[] => {
    return focuses.filter((focus) => {
      if (search !== '') {
        const s = search.toLowerCase();

        if (!focus.name.toLowerCase().includes(s)) {
          return false;
        }
      }

      return true;
    });
  }, [focuses, search]);

  const columns = React.useMemo(() => {
    return getColumns(t);
  }, [t]);

  const table = useReactTable({
    columns,
    data: rows,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <VStack width="full">
      <Primitive.div width="18rem">
        <Field.Root>
          <Field.Label htmlFor="search">{t('common.search')}</Field.Label>
          <Field.Content>
            <TextField
              id="search"
              name="search"
              type="text"
              fontSize="default"
              fontWeight="normal"
              placeholder={t('securityFocuses.focusesTable.focusName')}
              iconRight={<FontAwesomeIcon icon={faSearch} />}
              width="auto"
              value={search}
              onChange={(e) => {
                setSearch(e.currentTarget.value);
              }}
            />
          </Field.Content>
          <Field.Message />
        </Field.Root>
      </Primitive.div>
      {focuses.length === 0 ? (
        <EmptyResults
          variant="banner"
          icon={faInfoCircle}
          title={t('securityFocuses.focusesTable.emptyMessage')}
        />
      ) : (
        <DataTable table={table} stickyLastColumn />
      )}
    </VStack>
  );
};
